import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import Input from "../blocks/Input";
import Button from "../blocks/Button";
import {
  validateLoginForm,
  hasValidationErrors,
  validateEmail,
  validatePassword,
} from "../../utils/validation";
import AuthLink from "../blocks/AuthLink";
import Snackbar from "../blocks/Snackbar";
import SocialButton from "../blocks/SocialButton";
import Heading from "../blocks/Heading";
import NotFound from "./notFound";

const LoginComponent = () => {
  const {
    isAuthenticated,
    login,
    federatedLogin,
    isLoading,
    error,
    clearError,
    isRedirecting,
    setIsRedirecting,
  } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const hasCheckedAuth = useRef(false);

  const [touched, setTouched] = useState<Record<string, boolean>>({
    email: false,
    password: false,
  });

  const [errors, setErrors] = useState<Record<string, string | null>>({
    email: null,
    password: null,
  });

  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email && !validateEmail(value)) {
      setErrors((prev) => ({ ...prev, email: null }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) {
      const newError = validatePassword(value);
      setErrors((prev) => ({ ...prev, password: newError }));
    }
  };

  const handleEmailBlur = () => {
    setTouched((prev) => ({ ...prev, email: true }));
    const newErrors = validateEmail(email);
    setErrors((prev) => ({ ...prev, email: newErrors }));
  };

  const handlePasswordBlur = () => {
    setTouched((prev) => ({ ...prev, password: true }));
    const newErrors = validatePassword(password);
    setErrors((prev) => ({ ...prev, password: newErrors }));
  };

  const checkRateLimit = (): boolean => {
    if (loginAttempts >= 5) {
      setIsRateLimited(true);
      setTimeout(() => {
        setLoginAttempts(0);
        setIsRateLimited(false);
      }, 60000);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccessMessage(null);

    setTouched({ email: true, password: true });

    if (!checkRateLimit()) {
      setErrors({
        email: "Too many login attempts. Please try again in 1 minute.",
        password: null,
      });
      return;
    }

    const newErrors = validateLoginForm({ email, password });
    setErrors(newErrors);

    if (hasValidationErrors(newErrors)) return;

    try {
      setLoginAttempts((prev) => prev + 1);
      setSuccessMessage("Login successful! Redirecting to your profile...");
      setIsRedirecting(true);

      // Give Snackbar time to show before auth completes
      setTimeout(async () => {
        try {
          await login(email, password);
          setLoginAttempts(0);
        } catch (err) {
          setSuccessMessage(null);
          setIsRedirecting(false);
          console.error("Login error:", err);
        }
      }, 500);
    } catch (err) {
      console.error("Login error:", err);
    }
  };

  const handleGoogleLogin = async () => {
    clearError();
    setSuccessMessage(null);
    try {
      setSuccessMessage("Signing in with Google...");
      setIsRedirecting(true);
      await federatedLogin("Google");
    } catch (err) {
      setSuccessMessage(null);
      setIsRedirecting(false);
      console.error("Google login error:", err);
    }
  };

  const handleAppleLogin = async () => {
    clearError();
    setSuccessMessage(null);
    try {
      setSuccessMessage("Signing in with Apple...");
      setIsRedirecting(true);
      await federatedLogin("Apple");
    } catch (err) {
      setSuccessMessage(null);
      setIsRedirecting(false);
      console.error("Apple login error:", err);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (hasCheckedAuth.current) return;

    hasCheckedAuth.current = true;
  }, [isLoading, isAuthenticated]);

  if (isAuthenticated && !isRedirecting) {
    return <NotFound />;
  }

  if (isAuthenticated && isRedirecting) {
    return (
      <div className="auth__global__wrapper">
        <div className="authentication__component">
          <div className="auth_wrapper">
            <Heading
              heading="Login successful"
              text="Redirecting to your profile..."
            />
            <p className="text-size-regular">
              Please wait while we redirect you. If nothing happens,{" "}
              <a className="text_link" href="/profile">
                click here
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="authentication">
      <Heading
        heading="Welcome back!"
        text="Log in to view your profile and manage your settings."
      />
      <form className="authentication" onSubmit={handleSubmit}>
        <div className="authentication__top">
          {error && <Snackbar text={error} cleanErrorTimeout={clearError} />}
          {successMessage && (
            <Snackbar
              text={successMessage}
              cleanErrorTimeout={() => setSuccessMessage(null)}
            />
          )}

          <Input
            id="email-input"
            name="Email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            error={errors.email || undefined}
            isValid={!errors.email && email.length > 0}
            disabled={isLoading || isRateLimited}
            required
          />

          <Input
            id="password-input"
            name="Password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            onBlur={handlePasswordBlur}
            error={errors.password || undefined}
            isValid={!errors.password && password.length > 0}
            disabled={isLoading || isRateLimited}
            required
          >
            <AuthLink link="/auth/forgot-password" text={"Forgot Password"} />
          </Input>

          <div className="authentication__links__wrapper">
            <div className="authentication__links__box">
              <p className="text-size-regular">Don't have an account?</p>
              <a className="authentication__link" href="/register">
                Sign up
              </a>
            </div>
          </div>
        </div>

        <Button
          disabled={
            isLoading ||
            isRateLimited ||
            hasValidationErrors(errors) ||
            !email ||
            !password
          }
          text={isLoading ? "Logging in..." : "Log In"}
        />
      </form>
      <div className="authentication__social__wrapper">
        <div className="authentication__divider">
          <span className="authentication__line"> </span>
          <span className="text-size-regular">Or</span>
          <span className="authentication__line"> </span>
        </div>

        <div className="authentication__social">
          <SocialButton
            type="google"
            onClick={handleGoogleLogin}
            text={"Continue with Google"}
            disabled={isLoading || isRateLimited}
          />
          <SocialButton
            type="apple"
            onClick={handleAppleLogin}
            text={"Sign in with Apple"}
            disabled={isLoading || isRateLimited}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginComponent;
